import { mutate } from 'swr'

import {
  patchJson,
  postJson,
  putJson,
  requestDelete,
} from '../../../data/fetchers'
import {
  Attachment,
  DeliveryForecastDespatchAdvice,
  DeliveryForecastDespatchAdviceItem,
  DeliveryForecastDespatchAdviceItemBatch,
  DeliveryForecastDespatchAdvicePackaging,
  DeliveryForecastDespatchAdvicePackagingItem,
  PatchRequest,
  WebEdiDeliveryForecastDto,
} from '../../../data/models'

/**
 * Delivery forecast despatch advice
 */
// patch
export async function requestPatchDeliveryForecastDespatchAdviceById(
  despatchAdviceId: string,
  despatchAdviceDto: PatchRequest<
    WebEdiDeliveryForecastDto<DeliveryForecastDespatchAdvice>
  >,
): Promise<any> {
  const result = await patchJson(
    `/DeliveryForecastDespatchAdvices/${despatchAdviceId}`,
    despatchAdviceDto,
  )

  // HACK: Should be called in components in order to make OptimisticUi possible
  mutate((key: string) =>
    key
      .toLowerCase()
      .startsWith(
        `/DeliveryForecastDespatchAdvices/${despatchAdviceId}`.toLowerCase(),
      ),
  )

  return result
}

// complete
export async function requestCompleteDeliveryForecastDespatchAdviceById(
  despatchAdviceId: string,
  despatchAdviceDto: PatchRequest<
    WebEdiDeliveryForecastDto<DeliveryForecastDespatchAdvice>
  >,
): Promise<any> {
  return await patchJson(
    `/DeliveryForecastDespatchAdvices/${despatchAdviceId}/complete`,
    despatchAdviceDto,
  )
}

// put despatchAdviceItem
export async function requestPutDeliveryForecastDespatchAdviceItemById(
  despatchAdviceId: string,
  itemId: string,
  despatchAdviceDto: DeliveryForecastDespatchAdviceItem,
): Promise<any> {
  const result = await putJson(
    `/DeliveryForecastDespatchAdvices/${despatchAdviceId}/Items/${itemId}`,
    despatchAdviceDto,
  )

  // HACK: Should be called in components in order to make OptimisticUi possible
  mutate((key: string) =>
    key
      .toLowerCase()
      .startsWith(
        `/DeliveryForecastDespatchAdvices/${despatchAdviceId}`.toLowerCase(),
      ),
  )

  return result
}

/**
 * Attachments
 */
export async function requestAttachmentUpload(
  despatchAdviceId: string,
  processId: string,
  attachment: { attachment: File; category: string },
) {
  const { attachment: file, category } = attachment

  const attObj: Omit<Attachment, 'Id'> = {
    Name: file.name,
    Type: 'additional',
    Category: category,
    MimeType: file.type,
  }

  const formData = new FormData()
  formData.append('processId', processId)
  formData.append('file', file)
  formData.append('attachment', JSON.stringify(attObj))

  const result = await postJson(
    `/DeliveryForecastDespatchAdvices/${despatchAdviceId}/Attachments`,
    formData,
  )

  // HACK: Should be called in components in order to make OptimisticUi possible
  mutate((key: string) =>
    key
      .toLowerCase()
      .startsWith(
        `/DeliveryForecastDespatchAdvices/${despatchAdviceId}`.toLowerCase(),
      ),
  )

  return result
}

export async function requestAttachmentDelete(
  despatchAdviceId: string,
  attachmentId: string,
  attachmentUri: string,
) {
  const result = await requestDelete(
    `/DeliveryForecastDespatchAdvices/${despatchAdviceId}/Attachments/${attachmentId}`,
    {
      'Xia-AttachmentUri': encodeURI(attachmentUri),
    },
  )

  // HACK: Should be called in components in order to make OptimisticUi possible
  mutate((key: string) =>
    key
      .toLowerCase()
      .startsWith(
        `/DeliveryForecastDespatchAdvices/${despatchAdviceId}`.toLowerCase(),
      ),
  )

  return result
}

/**
 * Batches
 */
export async function requestCreateDeliveryForecastDespatchAdviceItemBatch(
  despatchAdviceId: string,
  itemId: string,
  batchDto: DeliveryForecastDespatchAdviceItemBatch,
) {
  const result = await postJson(
    `/DeliveryForecastDespatchAdvices/${despatchAdviceId}/Items/${itemId}/batches`,
    batchDto,
  )

  // HACK: Should be called in components in order to make OptimisticUi possible
  mutate((key: string) =>
    key
      .toLowerCase()
      .startsWith(
        `/DeliveryForecastDespatchAdvices/${despatchAdviceId}`.toLowerCase(),
      ),
  )

  return result
}

export async function requestUpdateDeliveryForecastDespatchAdviceItemBatch(
  despatchAdviceId: string,
  itemId: string,
  batchId: string,
  batchDto: DeliveryForecastDespatchAdviceItemBatch,
) {
  const result = await putJson(
    `/DeliveryForecastDespatchAdvices/${despatchAdviceId}/Items/${itemId}/batches/${batchId}`,
    batchDto,
  )

  // HACK: Should be called in components in order to make OptimisticUi possible
  mutate((key: string) =>
    key
      .toLowerCase()
      .startsWith(
        `/DeliveryForecastDespatchAdvices/${despatchAdviceId}`.toLowerCase(),
      ),
  )

  return result
}

export async function requestDeleteDeliveryForecastDespatchAdviceItemBatch(
  despatchAdviceId: string,
  itemId: string,
  batchId: string,
) {
  const result = await requestDelete(
    `/DeliveryForecastDespatchAdvices/${despatchAdviceId}/Items/${itemId}/batches/${batchId}`,
  )

  // HACK: Should be called in components in order to make OptimisticUi possible
  mutate((key: string) =>
    key
      .toLowerCase()
      .startsWith(
        `/DeliveryForecastDespatchAdvices/${despatchAdviceId}`.toLowerCase(),
      ),
  )

  return result
}

/**
 * Packaging
 */
// Create packaging table
export async function requestCreateDeliveryForecastDespatchAdviceItemPackaging(
  despatchAdviceId: string,
  itemId: string,
  packagingDto: DeliveryForecastDespatchAdvicePackaging,
) {
  const result = await postJson(
    `/deliveryforecastdespatchadvices/${despatchAdviceId}/items/${itemId}/packagings`,
    packagingDto,
  )

  // HACK: Should be called in components in order to make OptimisticUi possible
  mutate((key: string) =>
    key
      .toLowerCase()
      .startsWith(
        `/DeliveryForecastDespatchAdvices/${despatchAdviceId}`.toLowerCase(),
      ),
  )

  return result
}

// Put packaging table
export async function requestUpdateDeliveryForecastDespatchAdviceItemPackaging(
  despatchAdviceId: string,
  itemId: string,
  id: string,
  packagingDto: DeliveryForecastDespatchAdvicePackaging,
) {
  const result = await putJson(
    `/deliveryforecastdespatchadvices/${despatchAdviceId}/items/${itemId}/packagings/${id}`,
    packagingDto,
  )

  // HACK: Should be called in components in order to make OptimisticUi possible
  mutate((key: string) =>
    key
      .toLowerCase()
      .startsWith(
        `/DeliveryForecastDespatchAdvices/${despatchAdviceId}`.toLowerCase(),
      ),
  )

  return result
}

// Delete packaging table
export async function requestDeleteDeliveryForecastDespatchAdviceItemPackaging(
  despatchAdviceId: string,
  itemId: string,
  id: string,
) {
  const result = await requestDelete(
    `/deliveryforecastdespatchadvices/${despatchAdviceId}/items/${itemId}/packagings/${id}`,
  )

  // HACK: Should be called in components in order to make OptimisticUi possible
  mutate((key: string) =>
    key
      .toLowerCase()
      .startsWith(
        `/DeliveryForecastDespatchAdvices/${despatchAdviceId}`.toLowerCase(),
      ),
  )

  return result
}

// Create packaging item
export async function requestCreateDeliveryForecastDespatchAdviceItemPackagingItem(
  despatchAdviceId: string,
  itemId: string,
  packagingId: string,
  packagingDto: DeliveryForecastDespatchAdvicePackagingItem,
) {
  let url = `/deliveryforecastdespatchadvices/${despatchAdviceId}/items/${itemId}/packagings/${packagingId}/items`

  const result = await postJson(url, packagingDto)

  // HACK: Should be called in components in order to make OptimisticUi possible
  mutate((key: string) =>
    key
      .toLowerCase()
      .startsWith(
        `/DeliveryForecastDespatchAdvices/${despatchAdviceId}`.toLowerCase(),
      ),
  )

  return result
}

// Put packaging item
export async function requestUpdateDeliveryForecastDespatchAdviceItemPackagingItem(
  despatchAdviceId: string,
  itemId: string,
  packagingId: string,
  id: string,
  packagingDto: DeliveryForecastDespatchAdvicePackagingItem,
) {
  let url = `/deliveryforecastdespatchadvices/${despatchAdviceId}/items/${itemId}/packagings/${packagingId}/items/${id}`

  const result = await putJson(url, packagingDto)

  // HACK: Should be called in components in order to make OptimisticUi possible
  mutate((key: string) =>
    key
      .toLowerCase()
      .startsWith(
        `/DeliveryForecastDespatchAdvices/${despatchAdviceId}`.toLowerCase(),
      ),
  )

  return result
}

// Delete packaging item
export async function requestDeleteDeliveryForecastDespatchAdviceItemPackagingItem(
  despatchAdviceId: string,
  itemId: string,
  packagingId: string,
  id: string,
) {
  const result = await requestDelete(
    `/deliveryforecastdespatchadvices/${despatchAdviceId}/items/${itemId}/packagings/${packagingId}/items/${id}`,
  )

  // HACK: Should be called in components in order to make OptimisticUi possible
  mutate((key: string) =>
    key
      .toLowerCase()
      .startsWith(
        `/DeliveryForecastDespatchAdvices/${despatchAdviceId}`.toLowerCase(),
      ),
  )

  return result
}
